import React, { useMemo, useState } from 'react';
import { X, BookOpen, ChevronDown, ChevronUp, Tag } from 'lucide-react';
import { QuestionBankItem } from '../../types';
import questionBankData from '../../data/questionBank.json';

interface QuestionBankProps {
  jobTitle: string;
  onClose: () => void;
  isDarkMode?: boolean;
}

const QUESTIONS = questionBankData as QuestionBankItem[];

export const QuestionBank: React.FC<QuestionBankProps> = ({
  jobTitle,
  onClose,
  isDarkMode = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(QUESTIONS.map((q) => q.category)))],
    []
  );
  const difficulties = ['all', 'easy', 'medium', 'hard'];

  const filteredQuestions = useMemo(() => {
    return QUESTIONS.filter((question) => {
      const matchesSearch =
        question.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        question.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (question.sampleAnswer || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || question.category === selectedCategory;
      const matchesDifficulty =
        selectedDifficulty === 'all' || question.difficulty === selectedDifficulty;
      return matchesSearch && matchesCategory && matchesDifficulty;
    });
  }, [searchQuery, selectedCategory, selectedDifficulty]);

  const getDifficultyColor = (difficulty: string) => {
    if (isDarkMode) {
      switch (difficulty) {
        case 'easy':
          return 'bg-green-900/50 text-green-300';
        case 'medium':
          return 'bg-yellow-900/50 text-yellow-300';
        case 'hard':
          return 'bg-red-900/50 text-red-300';
        default:
          return 'bg-gray-800 text-gray-300';
      }
    }
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'hard':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category: string) => {
    if (isDarkMode) {
      switch (category) {
        case 'Technical':
          return 'bg-blue-900/50 text-blue-300';
        case 'Behavioral':
          return 'bg-purple-900/50 text-purple-300';
        case 'Experience':
          return 'bg-teal-900/50 text-teal-300';
        case 'HR':
          return 'bg-amber-900/50 text-amber-300';
        case 'General':
          return 'bg-gray-800 text-gray-300';
        default:
          return 'bg-gray-800 text-gray-300';
      }
    }
    switch (category) {
      case 'Technical':
        return 'bg-blue-100 text-blue-800';
      case 'Behavioral':
        return 'bg-purple-100 text-purple-800';
      case 'Experience':
        return 'bg-teal-100 text-teal-800';
      case 'HR':
        return 'bg-amber-100 text-amber-800';
      case 'General':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`h-full flex flex-col transition-colors ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}>
      <div
        className={`p-6 border-b flex justify-between items-center ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}
      >
        <div>
          <div className="flex items-center">
            <BookOpen className={`w-6 h-6 mr-2 ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`} />
            <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              Question Bank
            </h2>
          </div>
          {jobTitle ? (
            <p className={`text-sm mt-1 ml-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Practice prompts for {jobTitle}
            </p>
          ) : null}
        </div>
        <button
          onClick={onClose}
          className={`p-2 rounded-lg ${
            isDarkMode
              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
          aria-label="Close question bank"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div
        className={`p-4 flex flex-col sm:flex-row gap-3 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}
      >
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search questions, answers, or tags..."
          className={`flex-1 rounded-lg px-3 py-2 text-sm border focus:ring-2 focus:ring-primary-500 ${
            isDarkMode
              ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        />
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className={`rounded-lg px-3 py-2 text-sm border ${
            isDarkMode
              ? 'bg-gray-800 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        >
          {categories.map((c) => (
            <option key={c} value={c}>
              {c === 'all' ? 'All Categories' : c}
            </option>
          ))}
        </select>
        <select
          value={selectedDifficulty}
          onChange={(e) => setSelectedDifficulty(e.target.value)}
          className={`rounded-lg px-3 py-2 text-sm border ${
            isDarkMode
              ? 'bg-gray-800 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        >
          {difficulties.map((d) => (
            <option key={d} value={d}>
              {d === 'all' ? 'All Levels' : d}
            </option>
          ))}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {filteredQuestions.length === 0 ? (
          <p className={`text-sm text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No questions match your filters.
          </p>
        ) : (
          filteredQuestions.map((q) => {
            const isExpanded = expandedQuestion === q.id;
            return (
              <div
                key={q.id}
                className={`rounded-lg p-4 mb-4 border ${
                  isDarkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-white'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedQuestion(isExpanded ? null : q.id)}
                  className="w-full text-left"
                >
                  <div className="flex justify-between gap-3">
                    <div className="min-w-0">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(q.category)} mr-2`}
                      >
                        {q.category}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(q.difficulty)}`}
                      >
                        {q.difficulty}
                      </span>
                      <h3
                        className={`font-medium mt-2 ${
                          isDarkMode ? 'text-gray-100' : 'text-gray-900'
                        }`}
                      >
                        {q.question}
                      </h3>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {q.tags.map((tag) => (
                          <span
                            key={tag}
                            className={`inline-flex items-center text-xs px-2 py-0.5 rounded ${
                              isDarkMode
                                ? 'text-gray-400 bg-gray-800'
                                : 'text-gray-500 bg-gray-50'
                            }`}
                          >
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span className={`shrink-0 mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </span>
                  </div>
                </button>

                {isExpanded && q.sampleAnswer ? (
                  <div
                    className={`mt-3 p-3 rounded-lg text-sm leading-relaxed ${
                      isDarkMode
                        ? 'bg-blue-950/40 text-gray-200 border border-blue-900/50'
                        : 'bg-blue-50 text-gray-800'
                    }`}
                  >
                    <p
                      className={`text-xs font-semibold mb-1 ${
                        isDarkMode ? 'text-blue-300' : 'text-blue-700'
                      }`}
                    >
                      Sample answer
                    </p>
                    {q.sampleAnswer}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
